import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Trophy, TrendingUp, Award, Medal } from 'lucide-react';
import { formatNumber, formatAddress } from '../lib/utils';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const LeaderboardPage = () => {
  const [currentEpoch, setCurrentEpoch] = useState(null);
  const [epochLeaderboard, setEpochLeaderboard] = useState([]);
  const [alltimeLeaderboard, setAlltimeLeaderboard] = useState([]);
  const [category, setCategory] = useState('overall');
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    loadLeaderboards();
  }, [category]);
  
  const loadLeaderboards = async () => {
    try {
      setLoading(true);
      
      // Get current or latest epoch
      let epochRes = await axios.get(`${BACKEND_URL}/api/epochs/current`);
      let currentEpochData = epochRes.data;
      
      // If no active epoch, get all epochs and use the latest
      if (currentEpochData.message === "No active epoch") {
        const allEpochsRes = await axios.get(`${BACKEND_URL}/api/epochs`);
        const epochs = allEpochsRes.data.epochs;
        if (epochs && epochs.length > 0) {
          currentEpochData = epochs[0]; // Already sorted by epochId descending
        }
      }
      
      setCurrentEpoch(currentEpochData);
      
      if (currentEpochData.epochId) {
        // Get epoch leaderboard
        const epochLbRes = await axios.get(
          `${BACKEND_URL}/api/leaderboard/epoch/${currentEpochData.epochId}?category=${category}`
        );
        setEpochLeaderboard(epochLbRes.data.leaderboard);
      }
      
      // Get all-time leaderboard
      const alltimeLbRes = await axios.get(
        `${BACKEND_URL}/api/leaderboard/alltime?category=${category}`
      );
      setAlltimeLeaderboard(alltimeLbRes.data.leaderboard);
      
    } catch (error) {
      console.error('Error loading leaderboards:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const getRankIcon = (rank) => {
    if (rank === 1) return <Medal className="w-5 h-5 text-[#f9a825]" />;
    if (rank === 2) return <Medal className="w-5 h-5 text-[#666666]" />;
    if (rank === 3) return <Medal className="w-5 h-5 text-orange-600" />;
    return <span className="w-5 text-center text-slate-500">#{rank}</span>;
  };
  
  const LeaderboardTable = ({ data, type }) => {
    if (!data || data.length === 0) {
      return (
        <Card className="p-12 text-center border-[#b8b3ab] bg-[#e8e3db]/50">
          <Trophy className="w-16 h-16 mx-auto mb-4 text-slate-600" />
          <p className="text-[#666666]">No data available yet</p>
        </Card>
      );
    }
    
    return (
      <Card className="border-[#b8b3ab] bg-[#e8e3db]/50 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#d3cec6]/50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-[#666666] uppercase">Rank</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-[#666666] uppercase">Wallet</th>
                {type === 'epoch' && (
                  <>
                    <th className="px-6 py-4 text-right text-xs font-medium text-[#666666] uppercase">TRUE Points</th>
                    <th className="px-6 py-4 text-right text-xs font-medium text-[#666666] uppercase">Rewards</th>
                  </>
                )}
                {type === 'alltime' && (
                  <>
                    <th className="px-6 py-4 text-right text-xs font-medium text-[#666666] uppercase">Total Points</th>
                    <th className="px-6 py-4 text-right text-xs font-medium text-[#666666] uppercase">Total Earned</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {data.map((item, index) => (
                <tr key={item.wallet} className="hover:bg-[#d3cec6]/30 transition-colors" data-testid={`leaderboard-row-${index}`}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      {getRankIcon(index + 1)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap font-mono text-sm">
                    {formatAddress(item.wallet)}
                  </td>
                  {type === 'epoch' && (
                    <>
                      <td className="px-6 py-4 whitespace-nowrap text-right font-semibold text-[#f9a825]">
                        {formatNumber(item.totalTruePoints)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right font-semibold">
                        {formatNumber(item.totalReward)} TRUE
                      </td>
                    </>
                  )}
                  {type === 'alltime' && (
                    <>
                      <td className="px-6 py-4 whitespace-nowrap text-right font-semibold text-[#f9a825]">
                        {formatNumber(item.totalTruePoints)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right font-semibold">
                        {formatNumber(item.totalRewardsEarned)} TRUE
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    );
  };
  
  return (
    <div className="container mx-auto px-4 py-12" data-testid="leaderboard-page">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8">
          <Trophy className="w-16 h-16 mx-auto mb-4 text-[#f9a825]" />
          <h1 className="text-4xl font-bold mb-2">Leaderboard</h1>
          <p className="text-[#666666]">
            Top performers in TRUE Points rewards system
          </p>
        </div>
        
        {/* Category Selection */}
        <div className="flex justify-center gap-4 mb-8">
          <button
            onClick={() => setCategory('overall')}
            className={`px-6 py-3 rounded-lg font-medium transition-all ${
              category === 'overall'
                ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white'
                : 'bg-[#d3cec6]/50 text-[#666666] hover:bg-[#d3cec6]'
            }`}
            data-testid="category-overall"
          >
            <Trophy className="w-4 h-4 inline-block mr-2" />
            Overall
          </button>
          <button
            onClick={() => setCategory('tippers')}
            className={`px-6 py-3 rounded-lg font-medium transition-all ${
              category === 'tippers'
                ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white'
                : 'bg-[#d3cec6]/50 text-[#666666] hover:bg-[#d3cec6]'
            }`}
            data-testid="category-tippers"
          >
            <TrendingUp className="w-4 h-4 inline-block mr-2" />
            Top Tippers
          </button>
          <button
            onClick={() => setCategory('creators')}
            className={`px-6 py-3 rounded-lg font-medium transition-all ${
              category === 'creators'
                ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white'
                : 'bg-[#d3cec6]/50 text-[#666666] hover:bg-[#d3cec6]'
            }`}
            data-testid="category-creators"
          >
            <Award className="w-4 h-4 inline-block mr-2" />
            Top Creators
          </button>
        </div>
        
        {/* Tabs */}
        <Tabs defaultValue="epoch" className="w-full">
          <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-8">
            <TabsTrigger value="epoch" data-testid="tab-epoch">Current Epoch</TabsTrigger>
            <TabsTrigger value="alltime" data-testid="tab-alltime">All-Time</TabsTrigger>
          </TabsList>
          
          <TabsContent value="epoch">
            {currentEpoch && (
              <div className="mb-6 text-center">
                <p className="text-[#666666]">
                  Epoch {currentEpoch.epochId} • {currentEpoch.totalParticipants || 0} participants
                </p>
              </div>
            )}
            <LeaderboardTable data={epochLeaderboard} type="epoch" />
          </TabsContent>
          
          <TabsContent value="alltime">
            <LeaderboardTable data={alltimeLeaderboard} type="alltime" />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};
